import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import api from '../api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setVerificationStatus('error');
        setErrorMessage('No verification token found. Please check your email for the correct verification link.');
        return;
      }

      try {
        const response = await api.get(`/api/v1/users/verify-email/${token}`);
        
        if (response.status === 200) {
          setVerificationStatus('success');
        } else {
          setVerificationStatus('error');
          setErrorMessage('Verification failed. Please try again.');
        }
      } catch (error) {
        setVerificationStatus('error');
        
        if (error.response?.data?.detail) {
          setErrorMessage(error.response.data.detail);
        } else if (error.response?.status === 400) {
          setErrorMessage('Verification failed. The link may be invalid or expired. Please try registering again or contact support.');
        } else if (error.response?.status === 404) {
          setErrorMessage('User not found. Please check your verification link or try registering again.');
        } else {
          setErrorMessage('Verification failed. The link may be invalid or expired. Please try registering again or contact support.');
        }
      }
    };

    verifyEmail();
  }, [searchParams]);

  const renderContent = () => {
    switch (verificationStatus) {
      case 'verifying':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaSpinner className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Verifying Your Account</h3>
            <p className="text-gray-600">Please wait while we verify your email address...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaCheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Email Verified Successfully!</h3>
            <p className="text-gray-600 mb-6">
              Your email has been verified. You can now log in to your account and start using TrendVector.
            </p>
            <Link 
              to="/login" 
              className="inline-block bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-full transition-colors duration-200"
            >
              Go to Login
            </Link>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaTimesCircle className="w-10 h-10 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Verification Failed</h3>
            <p className="text-gray-600 mb-6">
              {errorMessage}
            </p>
            <div className="space-y-3">
              <Link 
                to="/signup" 
                className="inline-block bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-full transition-colors duration-200 mr-3"
              >
                Register Again
              </Link>
              <Link 
                to="/login" 
                className="inline-block bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-full transition-colors duration-200"
              >
                Go to Login
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen font-karla flex flex-col bg-gray-50">
      {/* Navbar Component */}
      <Navbar />
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-16 lg:py-12">
        <div className="max-w-md w-full mx-auto px-4">
          {/* Verification Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10">
            
            {/* Header Icon */}
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </div>
            </div>
            
            {/* Title */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">
                Email Verification
              </h2>
            </div>
            
            {/* Content */}
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default EmailVerificationPage;
